import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';

import {
  Combobox,
  ComboboxInput,
  ComboboxList,
  ComboboxItem,
  ComboboxEmpty,
  ComboboxFooter,
} from 'components/Combobox';
import { Label } from 'components/HookForm';
import Message from 'components/Message';
import { SvgArrow } from 'components/Svgs';
import When from 'components/When';
import { intlMessage } from 'constants/propTypes';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { getCategoryId, getCategoryMenuId, getCategoryName, getMenuId, getMenuName } from '../SkuMenuData.normalizers';
import { SKU_FLYOUT_DROPDOWN_MIN_MENU_HEIGHT, SKU_FLYOUT_DROPDOWN_VIEWPORT_MARGIN } from '../SkuItems.constants';

import messages from './SkuCategoryDropdown.messages';

const { element } = bem('item-form');

// Height of the flyout's sticky submit footer plus an 8px gap.
const FLYOUT_FOOTER_OFFSET = 80;

const SkuCategoryDropdown = ({
  label,
  groups,
  subgroups,
  value,
  onChange,
  onAddCategory,
  selectCategoryMessage,
  addCategoryMessage,
  disabled,
  required,
  showRequiredLabel,
}) => {
  const intl = useIntl();

  const menuNameById = useMemo(() => {
    const map = {};
    groups.forEach(menu => {
      map[getMenuId(menu)] = getMenuName(menu);
    });
    return map;
  }, [groups]);

  const selectedCategories = Array.isArray(value) ? value : [];
  const selectedCategoryIds = useMemo(() => selectedCategories.map(cat => getCategoryId(cat)), [selectedCategories]);

  const subgroupById = useMemo(() => {
    const map = {};
    subgroups.forEach(sub => {
      map[getCategoryId(sub)] = sub;
    });
    return map;
  }, [subgroups]);

  const handleChange = newIds => {
    const newCategories = newIds.map(id => subgroupById[id]).filter(Boolean);
    onChange(newCategories);
  };

  const handleRemoveCategory = categoryId => {
    onChange(selectedCategories.filter(cat => getCategoryId(cat) !== categoryId));
  };

  const emptyMessage = subgroups.length === 0 ? messages.noCategories : messages.noResults;

  return (
    <>
      <div className={element('category-dropdown')}>
        <Label message={label} required={required} showRequiredLabel={showRequiredLabel} />
        <When predicate={!disabled}>
          <Combobox
            value={selectedCategoryIds}
            onChange={handleChange}
            multiple
            menuBottomOffset={FLYOUT_FOOTER_OFFSET}
            menuViewportMargin={SKU_FLYOUT_DROPDOWN_VIEWPORT_MARGIN}
            menuMinHeight={SKU_FLYOUT_DROPDOWN_MIN_MENU_HEIGHT}>
            <ComboboxInput
              placeholder={intl.formatMessage(selectCategoryMessage || messages.searchPlaceholder)}
              prefix={<Isvg src={ICONS.searchGlassOutline} className={element('category-search-icon')} />}
              suffix={<SvgArrow direction="down" />}
              disabled={disabled}
            />
            <ComboboxList>
              {subgroups.map(subgroup => {
                const subgroupId = getCategoryId(subgroup);
                const isSelected = selectedCategoryIds.includes(subgroupId);
                return (
                  <ComboboxItem key={subgroupId} value={subgroupId} label={getCategoryName(subgroup)}>
                    <div className={element('category-item')}>
                      <div className={element('category-item-content')}>
                        <span className={element('category-name')}>{getCategoryName(subgroup)}</span>
                        <span className={element('category-menu-label')}>
                          {menuNameById[getCategoryMenuId(subgroup)]}
                        </span>
                      </div>
                      {isSelected && <Isvg src={ICONS.checkmark} className={element('category-selected-check')} />}
                    </div>
                  </ComboboxItem>
                );
              })}
              {onAddCategory && (
                <ComboboxFooter>
                  <button type="button" className={element('add-category-option')} onClick={onAddCategory}>
                    <Isvg src={ICONS.plus} className={element('add-category-plus')} />
                    <Message message={addCategoryMessage} />
                  </button>
                </ComboboxFooter>
              )}
            </ComboboxList>
            <ComboboxEmpty>
              <Message message={emptyMessage} />
            </ComboboxEmpty>
          </Combobox>
        </When>
      </div>
      <When predicate={selectedCategories.length > 0}>
        <div className={element('selected-categories')}>
          {selectedCategories.map(category => {
            const categoryId = getCategoryId(category);
            const categoryName = getCategoryName(category);
            const menuName = menuNameById[getCategoryMenuId(category)];
            return (
              <div key={categoryId} className={element('selected-category')}>
                <div className={element('selected-category-content')}>
                  <div className={element('selected-category-name')}>{categoryName}</div>
                  <div className={element('selected-category-menu')}>{menuName}</div>
                </div>
                <button
                  type="button"
                  className={element('selected-category-remove')}
                  aria-label={intl.formatMessage(messages.removeCategoryAriaLabel, { categoryName, menuName })}
                  onClick={disabled ? undefined : () => handleRemoveCategory(categoryId)}
                  disabled={disabled}>
                  <Message message={messages.removeCategory} />
                </button>
              </div>
            );
          })}
        </div>
      </When>
    </>
  );
};

SkuCategoryDropdown.propTypes = {
  label: intlMessage.isRequired,
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })
  ),
  subgroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      groupId: PropTypes.string,
    })
  ),
  value: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })
  ),
  onChange: PropTypes.func.isRequired,
  onAddCategory: PropTypes.func,
  selectCategoryMessage: intlMessage.isRequired,
  addCategoryMessage: intlMessage.isRequired,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  showRequiredLabel: PropTypes.bool,
};

SkuCategoryDropdown.defaultProps = {
  groups: [],
  subgroups: [],
  value: [],
  onAddCategory: null,
  required: false,
  showRequiredLabel: false,
};

export default SkuCategoryDropdown;
